import { useContext, useState } from "react";
import { ChildById } from "../ComponentRenderer";
import { ComponentProps } from "../registry";
import { FormSubmitContext, useBound } from "../helpers";
import { resolveChildRef } from "../../path";
import { dispatchAction } from "../../action";
import { ActionDescriptor, BoundValue, ChildRef } from "../../types";

export function Button({ fields, scope }: ComponentProps) {
  const primary = useBound<boolean>(fields.primary as BoundValue, scope);
  const childId = resolveChildRef(fields.child as ChildRef);
  const action = fields.action as ActionDescriptor | undefined;
  const actionName = useBound<string>(action?.name as BoundValue | undefined, scope);
  const formSubmitName = useContext(FormSubmitContext);
  const [pending, setPending] = useState(false);

  // A2UI semantics inside a Form: a Button whose action name equals the
  // form's submitAction name IS the submit affordance — it defers to the
  // form's onSubmit so exactly one dispatch fires per click. An actionless
  // button inside a form keeps native submit behavior. Every other button
  // is type="button" so it never implicitly submits the enclosing form
  // (HTML defaults <button> to type="submit").
  const isSubmit =
    formSubmitName !== null && (!action || actionName === formSubmitName);

  const onClick = async () => {
    if (isSubmit || !action || pending) return;
    setPending(true);
    try { await dispatchAction(action, scope); }
    finally { setPending(false); }
  };

  return (
    <button
      type={isSubmit ? "submit" : "button"}
      className={`a2-button${primary ? " primary" : ""}`}
      onClick={isSubmit ? undefined : onClick}
      disabled={pending}
    >
      <ChildById id={childId} scope={scope} />
    </button>
  );
}
